import React from 'react'
import { Box, Divider, Typography } from '@material-ui/core'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormGroup } from '../../../../form/FormGroup'
import { ControlledTextField } from '../../../../form/ControlledTextField'
import { validators } from '../../../../../validation/validators'
import {
  HeatingOrCoolingSource,
  HourSeriesCollectionFragment,
  HourSeriesType,
} from '../../../../../generated/graphql'
import { powerUnit, temperatureUnit } from '../../../../../util/formatters'
import { ControlledHourSeriesSelect } from '../../../../form/ControlledHourSeriesSelect'
import { InternalFormValues } from '../../form-values'
import { StandardHeatingOrCoolingSource } from './StandardHeatingOrCoolingSource'

type Props = {
  hourSeriesCollections: HourSeriesCollectionFragment[]
}

export function HeatCollectionOptions({
  hourSeriesCollections,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { control, errors } = useFormContext<InternalFormValues>()

  const baseHeatSource = useWatch<HeatingOrCoolingSource>({
    control,
    name: 'parameters.hybridHeatPump.baseHeatSource',
  })
  const wasteHeatSourceAHourSeriesId = useWatch<string>({
    control,
    name: 'parameters.hybridHeatPump.wasteHeatSourceAHourSeriesId',
  })
  const wasteHeatSourceBHourSeriesId = useWatch<string>({
    control,
    name: 'parameters.hybridHeatPump.wasteHeatSourceBHourSeriesId',
  })

  return (
    <Box flexGrow={1} display="flex" flexDirection="column">
      <FormGroup>
        <StandardHeatingOrCoolingSource
          control={control}
          name="parameters.hybridHeatPump.baseHeatSource"
          error={errors.parameters?.hybridHeatPump?.baseHeatSource}
          label={t('createProvider.forms.hybridHeatPump.baseHeatSource')}
        />
      </FormGroup>

      {baseHeatSource === HeatingOrCoolingSource.Ground && (
        <>
          <FormGroup>
            <ControlledTextField
              control={control}
              name="parameters.hybridHeatPump.groundHeatSourceTemperature"
              rules={{
                ...validators.required,
                ...validators.decimal,
              }}
              error={
                errors.parameters?.hybridHeatPump?.groundHeatSourceTemperature
              }
              label={t(
                'createProvider.forms.hybridHeatPump.groundHeatSourceTemperature',
              )}
              InputProps={{
                endAdornment: <Typography>{temperatureUnit}</Typography>,
              }}
            />
          </FormGroup>

          <FormGroup>
            <ControlledTextField
              control={control}
              name="parameters.hybridHeatPump.groundHeatSourceMaxPower"
              rules={{
                ...validators.maxPower,
              }}
              error={
                errors.parameters?.hybridHeatPump?.groundHeatSourceMaxPower
              }
              label={t(
                'createProvider.forms.hybridHeatPump.groundHeatSourceMaxPower',
              )}
              InputProps={{
                endAdornment: <Typography>{powerUnit}</Typography>,
              }}
            />
          </FormGroup>
        </>
      )}

      {baseHeatSource === HeatingOrCoolingSource.Air && (
        <FormGroup>
          <ControlledTextField
            control={control}
            name="parameters.hybridHeatPump.airHeatSourceMinTemperature"
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            error={
              errors.parameters?.hybridHeatPump?.airHeatSourceMinTemperature
            }
            label={t(
              'createProvider.forms.hybridHeatPump.airHeatSourceMinTemperature',
            )}
            InputProps={{
              endAdornment: <Typography>{temperatureUnit}</Typography>,
            }}
          />
        </FormGroup>
      )}

      <Box mb={2}>
        <Divider />
      </Box>

      <FormGroup>
        <ControlledHourSeriesSelect
          control={control}
          name="parameters.hybridHeatPump.wasteHeatSourceAHourSeriesId"
          error={
            errors.parameters?.hybridHeatPump?.wasteHeatSourceAHourSeriesId
          }
          label={t(
            'createProvider.forms.hybridHeatPump.wasteHeatSourceAHourSeriesId',
          )}
          collections={hourSeriesCollections}
          filter={(hourSeries) =>
            hourSeries.type === HourSeriesType.WasteHeating
          }
          single
          allowEmpty
        />
      </FormGroup>

      {wasteHeatSourceAHourSeriesId && (
        <FormGroup>
          <ControlledTextField
            control={control}
            name="parameters.hybridHeatPump.wasteHeatSourceATemperature"
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            error={
              errors.parameters?.hybridHeatPump?.wasteHeatSourceATemperature
            }
            label={t(
              'createProvider.forms.hybridHeatPump.wasteHeatSourceATemperature',
            )}
            InputProps={{
              endAdornment: <Typography>{temperatureUnit}</Typography>,
            }}
          />
        </FormGroup>
      )}

      <Box mb={2}>
        <Divider />
      </Box>

      <FormGroup>
        <ControlledHourSeriesSelect
          control={control}
          name="parameters.hybridHeatPump.wasteHeatSourceBHourSeriesId"
          error={
            errors.parameters?.hybridHeatPump?.wasteHeatSourceBHourSeriesId
          }
          label={t(
            'createProvider.forms.hybridHeatPump.wasteHeatSourceBHourSeriesId',
          )}
          collections={hourSeriesCollections}
          filter={(hourSeries) =>
            hourSeries.type === HourSeriesType.WasteHeating
          }
          single
          allowEmpty
        />
      </FormGroup>

      {wasteHeatSourceBHourSeriesId && (
        <FormGroup>
          <ControlledTextField
            control={control}
            name="parameters.hybridHeatPump.wasteHeatSourceBTemperature"
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            error={
              errors.parameters?.hybridHeatPump?.wasteHeatSourceBTemperature
            }
            label={t(
              'createProvider.forms.hybridHeatPump.wasteHeatSourceBTemperature',
            )}
            InputProps={{
              endAdornment: <Typography>{temperatureUnit}</Typography>,
            }}
          />
        </FormGroup>
      )}
    </Box>
  )
}
